import { AgDialog, useSaladBar } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { TextField } from '@mui/material';
import { useSetAtom } from 'jotai';
import { useState } from 'react';

import { useRedisWritesBlocked } from '@/apis/call';
import { checkSearchTemplates, createSearchTemplate } from '@/apis/searchtemplates';
import { queryClient } from '@/App';
import { SEARCH_TEMPLATE_DESCRIPTION_MAX_LEN, SEARCH_TEMPLATE_NAME_MAX_LEN } from '@/constants';
import { ApiBodyError, isDefinedError } from '@/errors';
import { SearchTreeNode } from '@/types';
import { searchTreeBuilderStateAtom } from '@pages/AdvancedSearchListCreatorPage';
import { searchTemplateForCopyAtom } from '@pages/SavedSearchesPage';
import { TemplatePlaceholderWithNodeAndFilterContext } from './types';
import { createSearchTemplateJson } from './util';

const SEARCH_TEMPLATE_DUPLICATE_MESSAGE = 'Template name is already in use.';

export type SaveTemplateDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  /** When save succeeds, called with the path to navigate to. Parent clears dirty and navigates. */
  onSaveSuccess?: (targetPath: string) => void;
  searchTree: SearchTreeNode | undefined;
  placeholdersData: Record<string, TemplatePlaceholderWithNodeAndFilterContext>;
  organisations: string[];
  /** Only the initial description because user can change it one last time
   * before saving */
  initialDescription: string;
};

export default function SaveTemplateDialog({
  isOpen,
  onClose,
  onSaveSuccess,
  searchTree,
  placeholdersData,
  organisations,
  initialDescription,
}: SaveTemplateDialogProps) {
  const { enqueueSuccessNotification, enqueueErrorNotification } = useSaladBar();
  const writesBlocked = useRedisWritesBlocked();
  const { getAccessTokenSilently, user } = useAuth0();
  const setSearchTreeBuilderState = useSetAtom(searchTreeBuilderStateAtom);
  const setSearchTemplateForCopyValue = useSetAtom(searchTemplateForCopyAtom);

  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState(initialDescription);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isSaveTemplateError, setIsSaveTemplateError] = useState(false);
  const [saveTemplateErrorText, setSaveTemplateErrorText] = useState('');
  const [isDuplicateDetected, setIsDuplicateDetected] = useState(false);
  const [isDescriptionTooShort, setIsDescriptionTooShort] = useState(false);

  const handleCreateSearchTemplate = async () => {
    if (writesBlocked) return;
    const userSub = user?.sub || '';
    try {
      setIsSavingTemplate(true);
      const searchTemplateJson = createSearchTemplateJson(searchTree, placeholdersData);
      const createResult = await createSearchTemplate(await getAccessTokenSilently(), {
        name: templateName,
        description: templateDescription,
        createdBySub: userSub,
        searchTemplateJson,
        organisations,
      });
      const createResultId = createResult.id;
      setSearchTreeBuilderState(undefined);
      setSearchTemplateForCopyValue(undefined);
      await queryClient.invalidateQueries({ queryKey: ['searchtemplates'] });
      enqueueSuccessNotification('The template has been saved.');
      const targetPath = `/template/${createResultId}`;
      onSaveSuccess?.(targetPath);
      onClose();
    } catch (e) {
      setIsSavingTemplate(false);
      setIsSaveTemplateError(true);
      enqueueErrorNotification('The template could not be saved.');
      if (
        e instanceof ApiBodyError &&
        isDefinedError(e) &&
        e.body.errors[0]?.name === 'CreateSearchTemplateDuplicateNameError'
      ) {
        setSaveTemplateErrorText(SEARCH_TEMPLATE_DUPLICATE_MESSAGE);
      } else {
        setSaveTemplateErrorText('There was an issue with the backend.');
      }
    }
  };

  const checkNameClashOnBlurOrEnter = async () => {
    if (!templateName) return;
    const token = await getAccessTokenSilently();
    const checkSearchResult = await checkSearchTemplates(token, { name: templateName });
    setIsDuplicateDetected(checkSearchResult);
  };

  const checkDescriptionOnBlurOrEnter = async () => {
    if (templateDescription.length < 3) {
      setIsDescriptionTooShort(true);
    } else {
      setIsDescriptionTooShort(false);
    }
  };

  const handleNameBlur = async () => {
    await checkNameClashOnBlurOrEnter();
  };

  const handleNameKeyPress = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      await checkNameClashOnBlurOrEnter();
    }
  };

  return (
    <AgDialog
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      dialogTitle="Save search template"
      primaryButton={{
        text: 'Save',
        onClick: async () => {
          await handleCreateSearchTemplate();
        },
        disabled:
          writesBlocked ||
          templateName.length < 3 ||
          isDescriptionTooShort ||
          isSavingTemplate ||
          isDuplicateDetected,
      }}
      secondaryButton={{
        text: 'Cancel',
        onClick: async () => {
          onClose();
        },
        disabled: isSavingTemplate,
      }}
      data-testid="qadCreateSearchTemplateDialog"
    >
      <TextField
        margin="dense"
        id="name"
        label={'Template name'}
        type="text"
        fullWidth
        variant="standard"
        value={templateName}
        onChange={(event) => setTemplateName(event.target.value)}
        slotProps={{
          htmlInput: {
            maxLength: SEARCH_TEMPLATE_NAME_MAX_LEN,
          },
        }}
        error={isDuplicateDetected ? true : isSaveTemplateError}
        helperText={isDuplicateDetected ? SEARCH_TEMPLATE_DUPLICATE_MESSAGE : saveTemplateErrorText}
        onBlur={handleNameBlur}
        onKeyUp={handleNameKeyPress}
      />
      <TextField
        margin="dense"
        id="description"
        label={'Description'}
        type="text"
        fullWidth
        variant="filled"
        multiline
        value={templateDescription}
        onChange={(event) => setTemplateDescription(event.target.value)}
        minRows={2}
        slotProps={{
          htmlInput: {
            maxLength: SEARCH_TEMPLATE_DESCRIPTION_MAX_LEN,
          },
        }}
        onBlur={checkDescriptionOnBlurOrEnter}
        onKeyUp={async (event) => {
          if (event.key === 'Enter') {
            await checkDescriptionOnBlurOrEnter();
          }
        }}
      />
    </AgDialog>
  );
}
